import {
  Modal,
  Drawer,
  Tabs,
  Box,
  LoadingOverlay,
  Alert,
  List,
  Stack,
  Text,
  CopyButton,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import { useMantineTheme, useMediaQuery } from 'hooks'
import {
  IconDeviceFloppy,
  IconAlertCircle,
} from '@tabler/icons-react'
import { Trans, useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Button } from 'components/Button'
import { ChangedDataConfirmModal } from 'components/ChangedDataConfirmModal'
import { useCurrentUser } from 'context'
import {
  useStyles,
  mobileDrawerStyles,
} from './AddStudentsModalStyles'
import { InviteCodeTab } from './InviteCodeTab'
import { AddManuallyTab } from './AddManuallyTab'
import { CreateGroupModal } from './CreateGroupModal'
import type { AddStudentsTab, BatchCreateResponse } from './interfaces'

const naturalSort = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
import { useAddStudentsModal } from '../../context/AddStudentsModalContext'

export function AddStudentsModal() {
  const { t } = useTranslation()
  const { classes } = useStyles()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(
    `(max-width: ${theme.breakpoints.sm})`,
  )
  const {
    opened,
    showDiscardModal,
    close,
    setShowDiscardModal,
    confirmDiscard,
    submitResult,
  } = useAddStudentsModal()

  const isSuccess =
    submitResult &&
    submitResult.failedStudents.length === 0 &&
    submitResult.addedStudents.length > 0

  const modalTitle = isSuccess
    ? t('students_added_modal.title')
    : t('add_students_modal.title')

  const shell = (
    <>
      <ModalContent isMobile={isMobile} />
      <ChangedDataConfirmModal
        opened={showDiscardModal}
        close={() => setShowDiscardModal(false)}
        discard={confirmDiscard}
      />
    </>
  )

  if (isMobile) {
    return (
      <Drawer
        opened={opened}
        onClose={close}
        position='bottom'
        className={classes.drawer}
        zIndex={100}
        withCloseButton={false}
        closeOnClickOutside
        closeOnEscape
        withOverlay
        transitionProps={{
          duration: 300,
          timingFunction: 'ease-out',
        }}
        styles={mobileDrawerStyles}
      >
        <Box className={classes.dragHandle} />
        {shell}
      </Drawer>
    )
  }

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={modalTitle}
      size='80%'
      centered
      className={classes.modal}
      zIndex={100}
    >
      {shell}
    </Modal>
  )
}

function SuccessContent({ result }: { result: BatchCreateResponse }) {
  const { t } = useTranslation()
  // The form rows are cleared right after a successful submit (AR-783), so
  // the groups shown here come from the snapshot taken at submit time.
  const { groups, submittedGroupIds } = useAddStudentsModal()

  const selectedGroupIds = new Set(submittedGroupIds)
  const groupName = groups
    .filter(g => selectedGroupIds.has(g.id))
    .map(g => g.name)
    .join(', ')

  return (
    <Stack spacing='md' p='xl'>
      <Text size='sm'>
        {t('students_added_modal.message', {
          count: result.addedStudents.length,
          group: groupName,
        })}
      </Text>
      <Text size='sm'>
        {t('students_added_modal.password_instruction')}
      </Text>

      <CopyButton value={result.defaultPassword}>
        {({ copied, copy }) => (
          <Tooltip
            label={copied ? t('students_added_modal.copied') : t('students_added_modal.copy_password')}
            withArrow
            opened={copied || undefined}
          >
            <UnstyledButton
              onClick={copy}
              sx={theme => ({
                backgroundColor: theme.colors.gray[1],
                borderRadius: theme.radius.md,
                padding: theme.spacing.md,
                textAlign: 'center',
                width: '100%',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: theme.colors.gray[2],
                },
              })}
            >
              <Text size='xs' color='dimmed' weight={600} transform='uppercase'>
                {t('students_added_modal.default_password_label')}
              </Text>
              <Text
                sx={{
                  fontSize: 32,
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  letterSpacing: 2,
                }}
              >
                {result.defaultPassword}
              </Text>
            </UnstyledButton>
          </Tooltip>
        )}
      </CopyButton>
    </Stack>
  )
}

function ModalContent({ isMobile }: { isMobile: boolean }) {
  const { t } = useTranslation()
  const { classes } = useStyles()
  const { currentUser } = useCurrentUser()
  const isManager = currentUser?.profile_used === 'manager'
  const [createGroupOpened, setCreateGroupOpened] = useState(false)
  const [createGroupLoading, setCreateGroupLoading] = useState(false)
  const {
    activeTab,
    setActiveTab,
    inviteCode,
    loading,
    savingStudents,
    students,
    groups,
    grades,
    updateStudent,
    updateStudentGroupIds,
    removeStudent,
    addStudent,
    submitResult,
    clearSubmitResult,
    close,
    submitStudents,
    canFillDownGrade,
    canFillDownGroup,
    fillDownGrade,
    fillDownGroup,
    createGroup,
  } = useAddStudentsModal()

  const handleCreateGroup = async (name: string) => {
    setCreateGroupLoading(true)
    try {
      await createGroup(name)
      setCreateGroupOpened(false)
    } catch {
      // error shown by context
    } finally {
      setCreateGroupLoading(false)
    }
  }

  const isSuccess =
    submitResult &&
    submitResult.failedStudents.length === 0 &&
    submitResult.addedStudents.length > 0

  if (isSuccess) {
    return <SuccessContent result={submitResult} />
  }

  const withData = students.filter(
    s => s.firstName.trim() || s.lastName.trim(),
  )

  const handleTab = (v: string | null) => {
    if (v) setActiveTab(v as AddStudentsTab)
  }

  const gradeOptions = grades
    .map(g => ({ value: g.id, label: g.name }))
    .sort((a, b) => naturalSort(a.label, b.label))

  const groupOptions = groups
    .map(g => ({ value: g.id, label: g.name }))
    .sort((a, b) => naturalSort(a.label, b.label))

  const hasFailed =
    submitResult && submitResult.failedStudents.length > 0

  // Managers can only add students manually — hide the tabs and
  // force the manual insertion view.
  const effectiveTab = isManager ? 'add-manually' : activeTab

  return (
    <>
      <LoadingOverlay
        visible={savingStudents}
        overlayBlur={2}
      />
      <Box
        className={
          isMobile
            ? classes.drawerContent
            : classes.tabsContainer
        }
      >
        <Tabs
          value={effectiveTab}
          onTabChange={handleTab}
          styles={{
            root: {
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
            },
            tabsList: {
              flexShrink: 0,
              padding: '0 32px',
            },
            panel: {
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {!isManager && (
            <Tabs.List>
              <Tabs.Tab value='invite-code'>
                {t('add_students_modal.tab_invite_code')}
              </Tabs.Tab>
              <Tabs.Tab value='add-manually'>
                {t('add_students_modal.tab_add_manually')}
                {withData.length > 0 && (
                  <CountBadge count={withData.length} />
                )}
              </Tabs.Tab>
            </Tabs.List>
          )}

          <Tabs.Panel
            value='invite-code'
            className={classes.tabPanel}
          >
            {hasFailed && (
              <FailedAlert
                result={submitResult}
                onClose={clearSubmitResult}
              />
            )}
            <InviteCodeTab
              inviteCode={inviteCode}
              loading={loading}
            />
          </Tabs.Panel>

          <Tabs.Panel
            value='add-manually'
            className={classes.tabPanel}
          >
            {hasFailed && (
              <FailedAlert
                result={submitResult}
                onClose={clearSubmitResult}
              />
            )}
            <AddManuallyTab
              grades={gradeOptions}
              groups={groupOptions}
              students={students}
              onStudentChange={updateStudent}
              onStudentGroupIdsChange={updateStudentGroupIds}
              onRemoveStudent={removeStudent}
              onAddStudent={addStudent}
              onFillDownGrade={fillDownGrade}
              onFillDownGroup={fillDownGroup}
              canFillDownGrade={canFillDownGrade}
              canFillDownGroup={canFillDownGroup}
              onCreateGroup={() => setCreateGroupOpened(true)}
            />
            <CreateGroupModal
              opened={createGroupOpened}
              onClose={() => setCreateGroupOpened(false)}
              onSubmit={handleCreateGroup}
              loading={createGroupLoading}
            />
          </Tabs.Panel>
        </Tabs>

        {effectiveTab === 'add-manually' && (
          <Box className={classes.footer}>
            <Button variant='default' onClick={close}>
              {t('add_students_modal.cancel')}
            </Button>
            <Button
              variant='filled'
              color='turquoise'
              onClick={submitStudents}
              loading={savingStudents}
              leftIcon={<IconDeviceFloppy size={18} />}
            >
              {t('add_students_modal.save')}
            </Button>
          </Box>
        )}
      </Box>
    </>
  )
}

function CountBadge({ count }: { count: number }) {
  return (
    <Box
      component='span'
      ml='xs'
      px={6}
      py={2}
      sx={theme => ({
        backgroundColor: theme.colors.turquoise[6],
        color: 'white',
        borderRadius: theme.radius.xl,
        fontSize: theme.fontSizes.xs,
        fontWeight: 600,
      })}
    >
      {count}
    </Box>
  )
}

function FailedAlert({
  result,
  onClose,
}: {
  result: { failedStudents: Array<{
    firstName: string
    lastName: string
    username: string
    field: string | null
    existingClasses: string[]
    reason: string
  }> } | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  if (!result) return null

  return (
    <Alert
      icon={<IconAlertCircle size={16} />}
      color='yellow'
      mb='md'
      withCloseButton
      onClose={onClose}
      title={t('add_students_modal.partial_success_title')}
    >
      <List size='sm'>
        {result.failedStudents.map((f, i) => (
          <List.Item key={i}>
            {f.field === 'username' && f.existingClasses.length > 0 ? (
              <Trans
                i18nKey='add_students_modal.username_exists_in_classes'
                values={{
                  name: `${f.firstName} ${f.lastName}`,
                  classes: f.existingClasses.join('; '),
                }}
                components={{ bold: <Text component='span' weight={700} size='sm' /> }}
              />
            ) : f.field === 'username' && f.username ? (
              <Trans
                i18nKey='add_students_modal.username_already_used'
                values={{ username: f.username }}
                components={{ bold: <Text component='span' weight={700} size='sm' /> }}
              />
            ) : (
              <>{f.firstName} {f.lastName}: {f.reason}</>
            )}
          </List.Item>
        ))}
      </List>
    </Alert>
  )
}
